import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mail, Clock, Feather, RefreshCw, X, Download, LogOut, Lock, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lcs_admin_token";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STAGE_LABEL = {
  conversing: "In conversation",
  style: "Choosing style",
  summary: "Reviewing summary",
  image_feedback: "Image revealed",
  email: "Awaiting email",
  complete: "Complete",
};

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function PasscodeGate({ onSuccess }) {
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!passcode.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (e) {
      setError(e.response?.status === 401 ? "Incorrect passcode. Please try again." : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-md w-full text-center"
      >
        <span className="inline-flex items-center gap-2 text-[#C5A059] font-sans text-xs tracking-[0.3em] uppercase">
          <Feather size={14} /> Lonesome Cactus Studio
        </span>
        <div className="mt-6 mb-2 flex justify-center">
          <span className="p-4 rounded-full bg-[#F1EBE1] text-[#114A4E]"><Lock size={26} /></span>
        </div>
        <h1 className="font-serif text-4xl text-[#0B2D30]">Studio Access</h1>
        <p className="font-sans text-base text-[#4A6C6D] mt-2">Enter your passcode to view story sessions.</p>
        <input
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Passcode"
          autoFocus
          data-testid="admin-passcode-input"
          className="mt-8 w-full text-center bg-transparent border-0 border-b-2 border-[#E8D8B0] focus:border-[#C5A059] text-[#0B2D30] text-2xl font-serif py-3 placeholder-[#4A6C6D]/40 transition-colors"
        />
        {error && <p className="mt-4 font-sans text-sm text-[#B23A48]" data-testid="admin-passcode-error">{error}</p>}
        <button
          onClick={submit}
          disabled={busy || !passcode.trim()}
          data-testid="admin-passcode-submit"
          className="mt-8 rounded-full bg-[#114A4E] text-[#FAF8F5] px-12 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans disabled:opacity-40"
        >
          {busy ? "Checking…" : "Enter studio"}
        </button>
        <div className="mt-8">
          <a href="/" className="font-sans text-sm text-[#114A4E] hover:text-[#C5A059] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={15} /> Back to session
          </a>
        </div>
      </motion.div>
    </div>
  );
}

function SummaryBlock({ label, children }) {
  return (
    <div className="mb-5">
      <h4 className="font-sans text-xs uppercase tracking-wider text-[#C5A059] mb-1">{label}</h4>
      <div className="font-sans text-base text-[#0B2D30] leading-relaxed whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function DetailDrawer({ id, token, onClose, onUnauthorized }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/admin/sessions/${id}`, authHeaders(token))
      .then(({ data }) => { if (active) { setDetail(data); setLoading(false); } })
      .catch((e) => {
        if (active) { setLoading(false); if (e.response?.status === 401) onUnauthorized(); }
      });
    return () => { active = false; };
  }, [id, token, onUnauthorized]);

  const data = detail?.data || {};

  const resend = async () => {
    setSending(true);
    setResendMsg(null);
    try {
      const { data: res } = await axios.post(`${API}/admin/sessions/${id}/resend`, {}, authHeaders(token));
      setResendMsg({ ok: res.status === "sent", text: res.detail || (res.status === "sent" ? "Email re-sent." : "Skipped.") });
    } catch (e) {
      if (e.response?.status === 401) { onUnauthorized(); return; }
      setResendMsg({ ok: false, text: e.response?.data?.detail || "Could not resend the email." });
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#0B2D30]/40 backdrop-blur-sm flex justify-end"
      onClick={onClose} data-testid="admin-detail-overlay"
    >
      <motion.div
        initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 60, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-xl h-full bg-[#FAF8F5] overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()} data-testid="admin-detail-panel"
      >
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-3xl text-[#0B2D30]">{data.first_name || "Untitled"}</h2>
          <button onClick={onClose} data-testid="admin-detail-close" className="p-2 rounded-full hover:bg-[#F1EBE1] transition-colors">
            <X size={22} className="text-[#114A4E]" />
          </button>
        </div>

        {loading ? (
          <p className="font-sans text-[#4A6C6D]">Loading…</p>
        ) : (
          <div>
            <div className="flex flex-wrap gap-3 mb-6 font-sans text-sm">
              {data.email && (
                <span className="inline-flex items-center gap-1.5 bg-[#F1EBE1] rounded-full px-3 py-1 text-[#114A4E]">
                  <Mail size={14} /> {data.email}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 bg-[#F1EBE1] rounded-full px-3 py-1 text-[#114A4E]">
                {STAGE_LABEL[detail.stage] || detail.stage}
              </span>
              {data.style && (
                <span className="inline-flex items-center gap-1.5 bg-[#F1EBE1] rounded-full px-3 py-1 text-[#114A4E]">{data.style}</span>
              )}
            </div>

            {data.image_base64 && (
              <img src={`data:image/png;base64,${data.image_base64}`} alt="Character"
                data-testid="admin-detail-image" className="w-full max-w-xs rounded-2xl border border-[#E8D8B0] mb-6" />
            )}

            {data.summary && <SummaryBlock label="Story Summary">{data.summary}</SummaryBlock>}
            {data.image_prompt && <SummaryBlock label="Image Prompt">{data.image_prompt}</SummaryBlock>}

            {data.email && (
              <div className="mb-2">
                <button
                  onClick={resend}
                  disabled={sending}
                  data-testid="admin-resend-email"
                  className="inline-flex items-center gap-2 rounded-full bg-[#114A4E] text-[#FAF8F5] px-6 py-3 hover:bg-[#0A3336] transition-colors duration-300 text-sm font-medium font-sans disabled:opacity-50"
                >
                  <Send size={16} /> {sending ? "Sending…" : "Resend story email"}
                </button>
                {resendMsg && (
                  <p
                    data-testid="admin-resend-status"
                    className={`mt-2 font-sans text-sm ${resendMsg.ok ? "text-[#114A4E]" : "text-[#B23A48]"}`}
                  >
                    {resendMsg.text}
                  </p>
                )}
              </div>
            )}

            <h4 className="font-sans text-xs uppercase tracking-wider text-[#C5A059] mt-8 mb-3">Transcript</h4>
            <div className="space-y-3" data-testid="admin-detail-transcript">
              {(detail.history || []).map((m, i) => (
                <div key={i} className={m.role === "guide" ? "" : "text-right"}>
                  <span className={`inline-block rounded-2xl px-4 py-2 font-sans text-sm leading-relaxed max-w-[85%] ${
                    m.role === "guide" ? "bg-[#114A4E] text-[#FAF8F5] rounded-tl-sm" : "bg-[#F1EBE1] text-[#0B2D30] rounded-tr-sm"
                  }`}>{m.content}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function Dashboard({ token, onLogout }) {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  }, [onLogout]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/sessions`, authHeaders(token))
      .then(({ data }) => { setSessions(data); setLoading(false); })
      .catch((e) => { setLoading(false); if (e.response?.status === 401) handleUnauthorized(); });
    axios.get(`${API}/admin/stats`, authHeaders(token))
      .then(({ data }) => setStats(data))
      .catch((e) => { if (e.response?.status === 401) handleUnauthorized(); });
  }, [token, handleUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/export`, { ...authHeaders(token), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "story_sessions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 401) handleUnauthorized();
    }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#E8D8B0] bg-[#FAF8F5]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-[#C5A059] font-sans text-xs tracking-[0.3em] uppercase">
              <Feather size={14} /> Lonesome Cactus Studio
            </span>
            <h1 className="font-serif text-3xl text-[#0B2D30] mt-1">Story Sessions</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={exportCsv} data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#C5A059] text-[#0B2D30] px-4 py-2 hover:bg-[#C5A059] hover:text-[#FAF8F5] transition-colors font-sans text-sm font-medium">
              <Download size={16} /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button onClick={load} data-testid="admin-refresh" className="p-2.5 rounded-full border border-[#E8D8B0] hover:bg-[#F1EBE1] transition-colors">
              <RefreshCw size={18} className="text-[#114A4E]" />
            </button>
            <button onClick={handleUnauthorized} data-testid="admin-logout" className="p-2.5 rounded-full border border-[#E8D8B0] hover:bg-[#F1EBE1] transition-colors" title="Log out">
              <LogOut size={18} className="text-[#114A4E]" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            {[
              { label: "Total sessions", value: stats.total },
              { label: "Completed", value: stats.completed },
              { label: "This week", value: stats.this_week },
              { label: "With email", value: stats.with_email },
            ].map((st) => (
              <div key={st.label} className="rounded-2xl border border-[#E8D8B0] bg-[#F1EBE1] px-5 py-4">
                <p className="font-serif text-4xl text-[#114A4E] leading-none">{st.value}</p>
                <p className="font-sans text-xs uppercase tracking-wider text-[#4A6C6D] mt-2">{st.label}</p>
              </div>
            ))}
          </div>
        )}
        {loading ? (
          <p className="font-sans text-[#4A6C6D]">Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <div className="text-center py-24" data-testid="admin-empty">
            <p className="font-serif text-2xl text-[#0B2D30]">No story sessions yet.</p>
            <p className="font-sans text-[#4A6C6D] mt-2">They'll appear here as clients begin their sessions.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-session-list">
            {sessions.map((s) => (
              <button key={s.session_id} onClick={() => setOpenId(s.session_id)}
                data-testid={`admin-session-row-${s.session_id}`}
                className="text-left rounded-2xl border border-[#E8D8B0] bg-[#FAF8F5] hover:border-[#C5A059] hover:shadow-md transition-all duration-300 p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-serif text-xl text-[#0B2D30]">{s.first_name || "Anonymous"}</p>
                  <p className="font-sans text-sm text-[#4A6C6D] truncate">
                    {s.email || "No email yet"}{s.style ? ` · ${s.style}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="font-sans text-xs rounded-full px-3 py-1 bg-[#F1EBE1] text-[#114A4E]">{STAGE_LABEL[s.stage] || s.stage}</span>
                  <span className="font-sans text-xs text-[#4A6C6D] hidden sm:inline-flex items-center gap-1.5"><Clock size={13} /> {formatDate(s.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {openId && <DetailDrawer id={openId} token={token} onClose={() => setOpenId(null)} onUnauthorized={handleUnauthorized} />}
      </AnimatePresence>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  if (!token) return <PasscodeGate onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={() => setToken(null)} />;
}
